import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { OnboardingProductsStore } from '../../../store/store';
import {
  CigoActions,
  ClientAdmissionActions,
  ReqManagerCreateActions,
  TriActions,
  UiActions
} from '../../../store/actions';

import { DataClientService } from '../../../services/data-client.service';
import { ContextService } from '../../../services/context.service';
import { LoadingService } from '../../../services/loading.service';
import { DatadogService } from '../../../services/datadog/datadog.service';
import { ContentfulService } from '../../../services/contentful.service';
import { OutputValues, ParentCallerService, ParentRequest } from '../../../services/parent-caller.service';
import { AccountFormatService } from '../../../services/account-format.service';
import { SessionService } from '../../../services/session.service';
import { DataNotClientService } from '../../../services/data-not-client.service';
import { FingerPrintHandler } from 'src/app/services/fingerprint.service';

import { OfferHandler } from './handler/step-offer.handler';
import { AccountIntention, ApiResponseStatus, CustomerDocumentType, FooterText, OfferMainImg, StepsClientTypes, StepsNotClientTypes } from '../../../shared/enums';

import { TagPage } from '../../../store/actions/ui.actions';
import { AccountsNames } from '../../../shared/interfaces/accountNames.interface';
import { RequestClientAdmission } from '../../../shared/interfaces/clientAdmission.interface';
import { RequestTri } from '../../../shared/interfaces/tri.interface';
import { RequestManagerCreate } from '../../../shared/interfaces/reqManagerCreate.interface';
import { ResponseCigoData } from '../../../shared/interfaces/cigo.interface';
import { OfferBenefits, OffersResponse } from 'src/app/shared/interfaces/offer.interface';
import { RateInfo } from 'src/app/shared/interfaces/contentfulInfo.interface';
import { FlagrService } from 'src/app/services/flagr/flagr.service';

@Component({
  selector: 'lib-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['../../../global.scss', './offer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OfferComponent implements OnInit, OnDestroy {
  private isMobile = environment.isMobile;
  private isWC = environment.isWc;
  public isMobileLayout = false;
  public rateInfo: RateInfo;
  public ratesValue = [];
  rateInfoTitle: string;
  offerImg: string;
  benefits: OfferBenefits[];
  subs = new Subscription();
  staticAssetsUrl = environment.staticScripts.staticAssets;
  mode = environment.mode;
  pageTitle: string;
  footerText: string;
  accountType: string;
  flowAccountName: string;
  accountsNames: AccountsNames;
  dataService: DataNotClientService | DataClientService;
  privateMode: boolean;
  offerBenefits$: OffersResponse;
  titleEnvironment = 'public';
  sessionStore: Storage;
  isRetake: boolean;

  triSubscriptions$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.tri)
  );
  cigoSubscriptions$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.cigo)
  );
  clientAdmission$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.clientAdmission)
  );
  createRequestManager$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.reqManagerCreate)
  );
  constructor(
    public loadingService: LoadingService,
    private store: Store<OnboardingProductsStore.AppState>,
    private dataClientService: DataClientService,
    private dataNotClientService: DataNotClientService,
    private dataDogService: DatadogService,
    private sessionService: SessionService,
    private accountFormatService: AccountFormatService,
    private parentCallerService: ParentCallerService,
    private contentfulService: ContentfulService,
    private clientOfferHandler: OfferHandler,
    private contextService: ContextService,
    private flagrService: FlagrService
  ) {}

  triSubscriptions = this.triSubscriptions$.subscribe(({ response, error }) => {
    if (!response) {
      return;
    }
    if (error !== null) {
      return this.actionCleanStateAndRedirect(TriActions);
    }
    if (response && response.client.operationState.code === ApiResponseStatus.OK) {
      this.loadingService.loading = false;
    } else {
      this.actionCleanStateAndRedirect(TriActions);
    }
  });

  cigoSubscriptions = this.cigoSubscriptions$.subscribe(({ response, error }) => {
    if (!response) {
      return;
    }
    // error case
    if (error !== null) {
      return this.actionCleanStateAndRedirect(CigoActions);
    }

    // succes case
    if (response) {
      this.store.dispatch(CigoActions.cleanState());
      if (this.dataClientService.isIsJointAccount()) {
        if (response.client.operationState.code === ApiResponseStatus.OK &&
          response.joint?.operationState.code === ApiResponseStatus.OK) {
            this.sessionStore.setItem('sessionId', response.client.operationState.sessionId);
            // Verifico que ambos sean clientes - Datos en cigo.
            if (response.joint.data && response.client.data) {
              this.setResponseCigoData(response.joint.data);
              this.setResponseCigoData(response.client.data);
            } else {
              return this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_DERIVATION }));
            }
            // Llamado al TRI si todo es correcto.
            this.callFingerprint();
          } else {
            return this.actionCleanStateAndRedirect(CigoActions);
          }
      } else if (response.client.operationState.code === ApiResponseStatus.OK) {
        this.sessionStore.setItem('sessionId', response.client.operationState.sessionId);
        this.setResponseCigoData(response.client.data);
        // Llamado al TRI si todo es correcto.
        this.callFingerprint();
      } else if (response.client.operationState.code === ApiResponseStatus.FAIL) {
        return this.actionCleanStateAndRedirect(CigoActions);
      } else if (response.client.operationState.code === ApiResponseStatus.ERROR) {
        return this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_UPDATE_DATA }));
      } else {
        const nextStep = this.privateMode ? StepsClientTypes.STEP_SERVICE_ERROR : StepsNotClientTypes.STEP_SERVICE_ERROR;
        return this.store.dispatch(UiActions.Navigate({ step: nextStep }));
      }
    }
  });

  clientAdmissionSubscriptions = this.clientAdmission$.subscribe(({ response, error }) => {
    if (!response) {
      return;
    }

    if (error !== null) {
      return this.actionCleanStateAndRedirect(ClientAdmissionActions);
    }
    if (response && response.client.operationState.code === ApiResponseStatus.OK) {
      this.sessionStore.setItem('sessionId', response.client.operationState.sessionId);
      this.store.dispatch(ClientAdmissionActions.cleanState());
      if (!this.dataService.isIsJointAccount() && this.privateMode) {
        const request = {
          client: {
            customerIdentificationNumber: this.sessionService.customerIdentificationNumber,
            customerIdentificationType: this.sessionService.customerIdentificationType
          }
        };
        this.store.dispatch(CigoActions.getCigo({ request}));
      } else {
        this.callFingerprint();
      }
    } else {
      this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_NO_OFFER }));
    }
  }
  );

  createRequestManagerSub = this.createRequestManager$.subscribe(({ response, error }) => {
    if (!response) {
      return;
    }
    if (error !== null) {
      return this.actionCleanStateAndRedirect(ReqManagerCreateActions);
    }
    if (response && response.client.operationState.code === ApiResponseStatus.OK) {
      this.store.dispatch(ReqManagerCreateActions.cleanState());
      this.store.dispatch(UiActions.Navigate({step: StepsClientTypes.STEP_NATIONALITY}));
    } else {
      this.actionCleanStateAndRedirect(ReqManagerCreateActions);
    }
  });

  ngOnDestroy() {
    this.clientAdmissionSubscriptions.unsubscribe();
    this.createRequestManagerSub.unsubscribe();
    this.cigoSubscriptions.unsubscribe();
    this.triSubscriptions.unsubscribe();
  }

  ngOnInit() {
    const isMobile = this.contextService.isMobile();
    this.sessionStore = isMobile ? sessionStorage : localStorage;
    this.sessionService.setIntention(this.getParsedIntention());
    this.getContentfulRateInfo();
    this.accountType = this.accountFormatService.getName();
    this.subs.add(this.clientOfferHandler.getOfferBenefits().subscribe((data) => {
      this.offerBenefits$ = data;
      this.setTextByAccountType();
    }));
    this.setService();
    this.accountFormatService.getAccountNameFromAssets().subscribe((accountTitle) => {
      this.pageTitle = accountTitle;
      this.flowAccountName = accountTitle;
    });
    this.footerText = FooterText[this.accountType];
    this.offerImg = this.staticAssetsUrl + OfferMainImg[this.accountType];
    this.dataClientService.setAccountType(this.accountType);
    this.accountsNames = this.accountFormatService.getAccountsNames();
    this.isMobileLayout = this.getIsMobile();
    window.onresize = () => {
      this.isMobileLayout = this.getIsMobile();
    };

    // Flujo por sitios privados
    if (this.privateMode) {
      this.titleEnvironment = 'private';
      this.loadingService.loading = true;
      if (!this.contextService.isMobile() && !this.dataService.isIsJointAccount()) {
        // Se setea session id en 1 para que el flujo no se rompa al agregar un header en null
        this.sessionStore.setItem('sessionId', '1');
      }
      this.dataService.setClientIsForeign(this.sessionService.customerIdentificationType === CustomerDocumentType.CE ? true : false);
      this.callClientAdmission();
    } else {
      setTimeout(() => {
        this.loadingService.loading = false;
      }, 100);
    }
  }

  callClientAdmission() {
    const parsedIntention = this.sessionService.intention;
    const request: RequestClientAdmission = this.dataService.isIsJointAccount() ? {
      client: {
        customerIdentificationNumber: this.sessionService.customerIdentificationNumber,
        customerIdentificationType: this.sessionService.customerIdentificationType,
        isForeign: this.sessionService.customerIdentificationType === CustomerDocumentType.CE ? true : false,
        intention: parsedIntention
      },
      joint: {
        customerIdentificationNumber : this.dataService.getIndentificationNumberJoint(),
        customerIdentificationType : this.dataService.getIdentificationTypeJoint(),
        isForeign : this.dataService.getIdentificationTypeJoint() === CustomerDocumentType.CE ? true : false
      }
    } : {
      client: {
        customerIdentificationNumber: this.sessionService.customerIdentificationNumber,
        customerIdentificationType: this.sessionService.customerIdentificationType,
        isForeign: this.sessionService.customerIdentificationType === CustomerDocumentType.CE ? true : false,
        intention: parsedIntention
      }
    };

    return this.store.dispatch(ClientAdmissionActions.getClientAdmission({ request }));
  }

  getContentfulRateInfo() {
    if (this.isMobile) {
      this.contentfulService.getEntriesFunction().subscribe((entryCollection) => {
        if (entryCollection) {
          this.rateInfo = entryCollection.items[0].fields;
          this.rateInfo.backgroundImage.fields.file.url = `http:${this.rateInfo.backgroundImage.fields.file.url}`;
          this.contentfulService.setRateInfo(this.rateInfo);
          this.loadingService.loading = true;
          this.rateInfo = this.contentfulService.getRateInfo();
          const splittedText = this.rateInfo.maintenanceRate.split(' ');
          for (let i = 0; i < splittedText.length; i++) {
            if (splittedText[i].includes('%')) {
              this.ratesValue.push(splittedText[i]);
            }
          }
        }
      });
    } else {
      this.rateInfo = this.contentfulService.getRateInfo();
      const splittedText = this.rateInfo.maintenanceRate.split(' ');
      for (let i = 0; i < splittedText.length; i++) {
        if (splittedText[i].includes('%')) {
          this.ratesValue.push(splittedText[i]);
        }
      }
    }
  }

  setService() {
    this.privateMode = (environment.mode === 'IN' || environment.mode === 'PF') ? true : false;
    this.dataService = this.privateMode ? this.dataClientService : this.dataNotClientService;
    this.isRetake = !this.privateMode ? this.dataNotClientService.getIsRetake() : false;
    const googleAnalytics = {is_joint_account: this.dataService.isIsJointAccount()};
    this.store.dispatch(TagPage({text: 'with-offer', extras: googleAnalytics}));
  }

  // Metodo para limpiar el estado luego de un error de servicio.
  actionCleanStateAndRedirect(action) {
    this.store.dispatch(action.cleanState());
    const nextStepError = this.privateMode ? StepsClientTypes.STEP_SERVICE_ERROR : StepsNotClientTypes.STEP_SERVICE_ERROR;
    this.store.dispatch(UiActions.Navigate({ step: nextStepError }));
    return;
  }

  setResponseCigoData(response: ResponseCigoData) {
    if (response.informacionContacto.correoElectronico && response.informacionContacto.telefono
        && response.informacionContacto.direccion) {
          this.dataClientService.setEmail(response.informacionContacto.correoElectronico[0].direccionCorreoElectronico);
          this.dataClientService.setCigoData(response);
    } else {
      this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_UPDATE_DATA }));
    }
  }

  callHealthcheck() {
    const request: ParentRequest = {
      operation: OutputValues.HEALTHCHECK,
      request: {}
    };
    this.parentCallerService.callParent(request);
  }

  continue() {
    this.loadingService.loading = true;
    if (environment.mode === 'IN' || environment.mode === 'PF') {
      this.continueInClientMode();
    } else if (environment.mode === 'WEB' || environment.mode === 'WEB-PF') {
      this.continueInNotClientMode();
    }
  }

  continueInClientMode() {
    if (environment.isWc) {
      this.callHealthcheck();
    }

    const request: RequestManagerCreate = {};
    this.dataDogService.addCustomEvent(`Offer accepted`, environment.mode, { channel: 'client' });
    this.store.dispatch(ReqManagerCreateActions.getReqManagerCreate({ request }));
  }

  async continueInNotClientMode() {
    this.dataDogService.addCustomEvent(`Offer accepted`, environment.mode, { channel: 'client' });
    let dniIsInFlagr = true;
    let redirect: StepsNotClientTypes = StepsNotClientTypes.STEP_ADDRESS_DATA;

    if (this.dataNotClientService.getIsRetake()) {
      // Se evalua si FLAGR esta prendido o no por static
      if (this.dataNotClientService.getUseFlagr()) {
        dniIsInFlagr = await this.flagrService.canShowFaceID();
      }
      if (environment.mode === 'WEB' && !dniIsInFlagr) {
        redirect = StepsNotClientTypes.STEP_FACE_RECOGNITION;
      } else {
        redirect = StepsNotClientTypes.STEP_TERMS_CONDITIONS;
      }
    }
    this.store.dispatch(UiActions.Navigate({ step: redirect }));
  }

  callFingerprint() {
    const fingerPrintHandler = new FingerPrintHandler();
    fingerPrintHandler.getFingerprint().subscribe((fingerprint) => {
      const device = JSON.parse(window.atob(fingerprint));
      this.callTri(device, fingerprint);
    });
  }

  callTri(device, fingerprint) {
    const identifier = uuidv4();
    const request: RequestTri = {
      device,
      fingerprint,
      identifier
    };
    this.store.dispatch(TriActions.getTri({ request }));
  }

  setTextByAccountType() {
    switch (this.accountType) {
      case 'Ahorro':
        this.rateInfoTitle = this.offerBenefits$.bannerTexts['ahorro'][0];
        this.benefits = this.offerBenefits$.items.classicBenefits;
        break;
      case 'Flex':
        this.benefits = this.offerBenefits$.items.falabellaBenefits;
        this.rateInfoTitle = this.offerBenefits$.bannerTexts['ahorro'][0];
        this.rateInfoTitle = this.offerBenefits$.bannerTexts['falabella'][0];
        break;
      case 'CTS':
        this.benefits = this.offerBenefits$.items.CTSBenefits;
        break;
      case 'AhorroSueldo':
        this.benefits = this.offerBenefits$.items.EmployeeBenefits;
        this.rateInfoTitle = this.offerBenefits$.bannerTexts['ahorroSueldo'][0];
        break;
      default:
        this.rateInfoTitle = this.offerBenefits$.bannerTexts['falabella'][0];
        break;
    }
  }

  checkRate() {
    window.open(this.rateInfo.rateLink, '_blank');
  }

  getIsMobile(): boolean {
    const w = document.documentElement.clientWidth;
    const breakpoint = 490;
    return w < breakpoint ? true : false;
  }

  getParsedIntention(): string {
    let parseIntention = '';
    switch (this.sessionService.intention) {
      case 'cts':
        parseIntention = AccountIntention.CTS;
        break;
      case 'ahorro':
        parseIntention = AccountIntention.AHORRRO;
        if (this.dataService && !!this.dataService.isIsJointAccount()) {
          parseIntention = AccountIntention.MANCOMUNADA;
        }
        break;
      case 'sueldo':
        parseIntention = AccountIntention.SUELDO;
        break;
      case 'ahorro-sueldo':
        parseIntention = AccountIntention.AHORRO_SUELDO;
        break;
      case 'ahorro-clasica':
          parseIntention = AccountIntention.AHORRRO;
        break;
      case 'ahorro-falabella':
          parseIntention = AccountIntention.SUELDO;
        break;
      default:
        parseIntention = this.sessionService.sessionAccountType;
        break;
    }
    return parseIntention;
  }

  getIsWC(): boolean {
    const isWc = !!this.isWC;
    return isWc;
  }
}
